import { navigate, PageProps } from 'gatsby';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, MenuButton } from '@opswat/ui';
import { PageHeader } from 'components/layout/PageHeader';
import { Footer } from 'components/footer/Footer';
import { useUser } from 'services/User';
import { canAccessHomePage, canAccessPage } from 'services/accessControl';
import { Logo } from 'components/layout/Logo';

type PublicLayoutProps = Omit<PageProps, 'children'> & { children?: ReactNode; };

export const PublicLayout = ({ children, location }: PublicLayoutProps) => {
    const pathname = location?.pathname;
    const { user } = useUser();
    const { t } = useTranslation();

    const isNavActive = (basePath: string) =>
        Boolean(pathname) && (pathname === basePath || pathname.startsWith(`${basePath}/`));

    const navButtons = [
        { text: t('Product'), basePath: '/product', onClick: () => navigate('/product') },
        { text: t('Integrations'), basePath: '/integrations', onClick: () => navigate('/integrations/all') },
        { text: t('Blog'), basePath: '/blog', onClick: () => navigate('/blog') },
        { text: t('Licensing'), basePath: '/licensing', onClick: () => navigate('/licensing') },
        ...(canAccessPage(user, 'store')
            ? [{ text: t('Store'), basePath: '/store', onClick: () => navigate('/store') }]
            : []),
    ];

    return (
        <div className='tw:font-roboto tw:w-screen tw:h-screen tw:flex tw:flex-col'>
            <header
                className='tw:flex tw:flex-row tw:justify-between tw:items-center tw:w-full tw:h-60 tw:px-20 tw:border-b tw:border-neutrals-200 tw:dark:border-neutrals-1000'
                role='banner'
            >
                <Logo />
                <div className='tw:flex tw:flex-row tw:items-center tw:self-stretch tw:gap-20'>
                    {navButtons.map(({ basePath, ...props }) => {
                        const active = isNavActive(basePath);
                        return (
                            <div key={props.text} className='tw:relative tw:flex tw:items-center tw:self-stretch'>
                                <Button
                                    variant='tertiary'
                                    aria-current={active ? 'page' : undefined}
                                    className={
                                        active
                                            ? 'tw:text-blue-700 tw:dark:text-blue-400'
                                            : 'tw:text-neutrals-800 tw:dark:text-neutrals-500 tw:hover:text-neutrals-1200 tw:dark:hover:text-white'
                                    }
                                    {...props}
                                />
                                {active && (
                                    <span
                                        aria-hidden='true'
                                        className='tw:absolute tw:inset-x-0 tw:bottom-0 tw:h-2 tw:bg-blue-700 tw:dark:bg-blue-400'
                                    />
                                )}
                            </div>
                        );
                    })}
                    {user?.isLoggedIn && (
                        <MenuButton>
                            <MenuButton.Trigger
                                onClick={() => navigate('/dashboard/security')}
                                data-testid='public-layout-dashboard-button'
                                className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                            >
                                <Icon iconName='dashboard' />
                            </MenuButton.Trigger>
                        </MenuButton>
                    )}
                    {pathname !== '/' && (
                        <MenuButton>
                            <MenuButton.Trigger
                                onClick={() => {
                                    if (canAccessHomePage(user)) {
                                        navigate('/', { state: { fromHomeButton: true } });
                                    } else {
                                        navigate('/dashboard/security');
                                    }
                                }}
                                data-testid='public-layout-home-button'
                                className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                            >
                                <Icon iconName='home' />
                            </MenuButton.Trigger>
                        </MenuButton>
                    )}
                    <PageHeader />
                </div>
            </header>
            <main className='tw:w-full tw:flex tw:flex-col tw:flex-1 tw:overflow-y-auto tw:overflow-x-hidden'>
                <div className='tw:flex-1'>{children}</div>
                {!pathname?.startsWith('/results') && <Footer />}
            </main>
        </div>
    );
};
